import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@Auth/useAuth";
import { useDrawer } from "@hooks/useDrawer";
import { DRAWER_CONTEXT } from "@models/DrawerContext";
import { useConfirmationModalStore } from "@stores/confirmationModalStore";
import { useTagsStore } from "@stores/tagsStore";
import { onErrorShow } from "@utils/onErrorShow";
import { onInfoShow } from "@utils/onInfoShow";
import { onSuccessShow } from "@utils/onSuccessShow";
import {
  CustomFieldsClient,
  ICustomFieldDefinition,
  ICustomFieldValue,
} from "@Clients/CustomFieldsClient";
import { useProduct } from "@features/products/queries/useProducts";
import {
  useCreateProduct,
  useUpdateProduct,
} from "@features/products/queries/useProductMutations";

export type CustomFieldValue = string | number | boolean | null | undefined;

interface ProductValues {
  name: string;
  sku: string;
  description: string;
  price: string;
  tags: string[];
  customFields: Record<string, CustomFieldValue>;
}

const EMPTY: ProductValues = {
  name: "",
  sku: "",
  description: "",
  price: "",
  tags: [],
  customFields: {},
};

function readCustomValue(value: ICustomFieldValue): CustomFieldValue {
  if (value.value_text != null) return value.value_text;
  if (value.value_number != null) return value.value_number;
  if (value.value_bool != null) return value.value_bool;
  if (value.value_date != null) return value.value_date;
  if (value.value_json != null) return value.value_json as CustomFieldValue;
  return null;
}

/**
 * View-model for the create/edit product drawer. Owns the core fields, the
 * org-defined custom fields (definitions + values), tags, validation, and the
 * save path — including the `createFromStockView` quick-create used by the
 * stock form's nested "New Product" drawer.
 */
export function useProductForm(createFromStockView: boolean) {
  const { close, context, selectedId } = useDrawer();
  const { setTouch } = useConfirmationModalStore();
  const { baseUser } = useAuth();
  const orgId = baseUser?.organization_id ?? "";

  const isEdit = context === DRAWER_CONTEXT.EDIT_PRODUCT;
  const { data: editingProduct } = useProduct(
    isEdit ? selectedId ?? undefined : undefined
  );
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const { tagValues, addProductTag, getProductTags, removeProductTag } =
    useTagsStore();

  const [values, setValues] = useState<ProductValues>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [customColumns, setCustomColumns] = useState<ICustomFieldDefinition[]>(
    []
  );

  const currentTagsRef = useRef<string[]>([]);
  const prefilledIdRef = useRef<string | null>(null);

  const touch = () => {
    if (!createFromStockView) setTouch(true);
  };

  const setField = <K extends keyof ProductValues>(
    field: K,
    value: ProductValues[K]
  ) => {
    setValues((v) => ({ ...v, [field]: value }));
    setErrors((e) => ({ ...e, [field as string]: "" }));
    touch();
  };

  const setCustomField = (name: string, value: CustomFieldValue) => {
    setValues((v) => ({
      ...v,
      customFields: { ...v.customFields, [name]: value },
    }));
    touch();
  };

  // Custom field definitions for products.
  useEffect(() => {
    if (!orgId) return;
    CustomFieldsClient.getDefinitionsByOrganizationId(orgId, "product")
      .then(setCustomColumns)
      .catch((e) => console.error("Error fetching product custom fields:", e));
  }, [orgId]);

  // Prefill once per edited product (re-dispatching would clobber edits while
  // the cached query settles). Reset when leaving the edit context.
  useEffect(() => {
    if (!isEdit || !selectedId) {
      prefilledIdRef.current = null;
      return;
    }
    if (prefilledIdRef.current === selectedId || !editingProduct) return;
    prefilledIdRef.current = selectedId;

    setValues((v) => ({
      ...v,
      name: editingProduct.name,
      sku: editingProduct.sku,
      description: editingProduct.description ?? "",
      price: editingProduct.price?.toString() ?? "",
    }));

    getProductTags(selectedId).then((productTags) => {
      const ids = productTags.map((t) => t.tag_values.id);
      currentTagsRef.current = ids;
      setValues((v) => ({ ...v, tags: ids }));
    });
  }, [isEdit, selectedId, editingProduct, getProductTags]);

  // Custom field values for the edited product, keyed by field name.
  useEffect(() => {
    if (!isEdit || !selectedId || !orgId || customColumns.length === 0) return;
    CustomFieldsClient.getValuesByEntity(orgId, "product", selectedId)
      .then((vals) => {
        const nameById = new Map(customColumns.map((c) => [c.id, c.name]));
        const mapped: Record<string, CustomFieldValue> = {};
        vals.forEach((val) => {
          const fieldName = nameById.get(val.field_id);
          if (fieldName) mapped[fieldName] = readCustomValue(val);
        });
        setValues((v) => ({ ...v, customFields: mapped }));
      })
      .catch((e) =>
        console.error("Error fetching product custom field values:", e)
      );
  }, [isEdit, selectedId, orgId, customColumns]);

  const selectedTags = useMemo(
    () => tagValues.filter((t) => values.tags.includes(t.id)),
    [tagValues, values.tags]
  );

  const addTag = (tagId: string) => {
    if (values.tags.includes(tagId)) return;
    setValues((v) => ({ ...v, tags: [...v.tags, tagId] }));
    touch();
  };

  const removeTag = async (tagId: string) => {
    if (selectedId && currentTagsRef.current.includes(tagId)) {
      await removeProductTag(selectedId, tagId);
      const productTags = await getProductTags(selectedId);
      currentTagsRef.current = productTags.map((t) => t.tag_values.id);
      onSuccessShow("Tag removed successfully");
    }
    setValues((v) => ({ ...v, tags: v.tags.filter((t) => t !== tagId) }));
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!values.name.trim()) next.name = "Name is required";
    if (values.price && Number(values.price) <= 0)
      next.price = "Price must be greater than zero";
    for (const column of customColumns) {
      if (!column.required) continue;
      const value = values.customFields[column.name];
      const empty =
        value === null ||
        value === undefined ||
        (typeof value === "string" && !value.trim());
      if (empty) next[`custom_${column.name}`] = `${column.label} is required`;
    }
    setErrors(next);
    if (Object.keys(next).length > 0) {
      onErrorShow("Please fix the errors before submitting");
      return false;
    }
    return true;
  };

  const buildCustomFieldPayload = (entityId: string) =>
    customColumns
      .map((column) => {
        const raw = values.customFields[column.name];
        const empty =
          raw === null ||
          raw === undefined ||
          (typeof raw === "string" && raw.trim() === "");
        if (empty) return null;

        const base = {
          organization_id: orgId,
          entity_type: "product" as const,
          entity_id: entityId,
          field_id: column.id,
          value_text: null,
          value_number: null,
          value_bool: null,
          value_date: null,
          value_json: null,
        };
        switch (column.type) {
          case "number":
            return { ...base, value_number: raw === "" ? null : Number(raw) };
          case "boolean":
            return { ...base, value_bool: Boolean(raw) };
          case "date":
            return { ...base, value_date: String(raw) };
          default:
            return { ...base, value_text: String(raw) };
        }
      })
      .filter(Boolean) as Array<
      Omit<ICustomFieldValue, "id" | "created_at" | "updated_at">
    >;

  const submit = async () => {
    if (!validate()) return;
    const { name, sku, description, price, tags } = values;
    const effectiveSku = sku.trim() || name.trim();
    setSubmitting(true);
    try {
      if (isEdit && selectedId) {
        await updateProduct.mutateAsync({
          id: selectedId,
          name,
          description,
          price: price ? Number(price) : null,
          sku: effectiveSku,
          organization_id: orgId,
        });
        await CustomFieldsClient.deleteValuesForEntity(orgId, "product", selectedId);
        const payload = buildCustomFieldPayload(selectedId);
        if (payload.length > 0) await CustomFieldsClient.upsertValues(payload);

        const newTags = tags.filter((t) => !currentTagsRef.current.includes(t));
        await Promise.all(newTags.map((t) => addProductTag(selectedId, t)));
        onInfoShow("Product updated successfully");
      } else {
        const created = await createProduct.mutateAsync({
          name,
          description,
          price: price ? Number(price) : null,
          sku: effectiveSku,
          organization_id: orgId,
        });
        const payload = buildCustomFieldPayload(created.id);
        if (payload.length > 0) await CustomFieldsClient.upsertValues(payload);
        if (!createFromStockView)
          await Promise.all(tags.map((t) => addProductTag(created.id, t)));
        onInfoShow("Product created successfully");
      }
      setTouch(false);
      close();
    } catch (error) {
      console.error(error);
      onErrorShow("Failed to save product");
    } finally {
      setSubmitting(false);
    }
  };

  return {
    isEdit,
    values,
    errors,
    submitting,
    customColumns,
    tagValues,
    selectedTags,
    setField,
    setCustomField,
    addTag,
    removeTag,
    submit,
  };
}
